// need a place to store global notifications and have an action to load current global notifications

import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { ACKNOWLEDGED_POPUPS_KEY } from '~/constants'
import type { GlobalNotification } from '~/services/apiClient'
import { GlobalNotificationDisplayType } from '~/services/apiClient'
import { useUserStore } from '~/stores/user'
import { fastParseISO } from '~/utils'

/**
 * Maps notification id -> the notification's `active_to` (ISO string or null)
 * as of acknowledgement time. A null `active_to` means the notification never
 * expires, so the acknowledgement is kept indefinitely.
 */
export type UserAcknowledgedPopups = Record<number, string | null>

/**
 * Maps user id -> that user's popup acknowledgements. All users' entries live
 * in one localStorage key (ACKNOWLEDGED_POPUPS_KEY) that deliberately
 * survives logout and org switches — see the keep-sets in the user store.
 *
 * An acknowledgement sticks to the notification id: content edits do NOT
 * re-prompt users who already acknowledged. Switching a notification's
 * display type back to banner clears its acknowledgements for all users (see
 * the watcher below), so admins can force a re-prompt by toggling popup ->
 * banner -> popup — or, to reach offline users too, by expiring the
 * notification and publishing a new one.
 */
export type AcknowledgedPopups = Record<number, UserAcknowledgedPopups>

export const useGlobalNotificationsStore = defineStore(
  'globalNotifications',
  () => {
    const userStore = useUserStore()
    const notifications = ref<Map<number, GlobalNotification>>(new Map())

    // userInfo loads asynchronously after login — keep this reactive
    const currentUserId = computed(() => userStore.userInfo?.id)

    function update(notification: GlobalNotification): void {
      notifications.value.set(notification.id, notification)
    }

    const activeNotifications = computed((): GlobalNotification[] => {
      return Array.from(notifications.value.values()).filter(
        (n: GlobalNotification) => {
          if (fastParseISO(n.active_from) > DateTime.now()) return false
          if (!n.active_to) return true
          return fastParseISO(n.active_to) > DateTime.now()
        }
      )
    })

    /**
     * Drops acknowledgements (across ALL users' sub-maps) whose notification
     * has expired, along with malformed entries and user sub-maps left empty.
     * If we currently know the notification, its live `active_to` wins over
     * the stored one (the expiry may have been edited since acknowledgement).
     */
    function pruneExpiredAcknowledgements(
      acknowledged: AcknowledgedPopups
    ): AcknowledgedPopups {
      const pruned: AcknowledgedPopups = {}
      for (const [userIdString, userAcks] of Object.entries(acknowledged)) {
        if (typeof userAcks !== 'object' || userAcks === null) continue
        const prunedUserAcks: UserAcknowledgedPopups = {}
        for (const [idString, storedActiveTo] of Object.entries(userAcks)) {
          if (storedActiveTo !== null && typeof storedActiveTo !== 'string') {
            continue
          }
          const id = Number(idString)
          const currentNotification = notifications.value.get(id)
          const activeTo = currentNotification
            ? (currentNotification.active_to ?? null)
            : storedActiveTo
          if (activeTo === null || fastParseISO(activeTo) > DateTime.now()) {
            prunedUserAcks[id] = activeTo
          }
        }
        if (Object.keys(prunedUserAcks).length > 0) {
          pruned[Number(userIdString)] = prunedUserAcks
        }
      }
      return pruned
    }

    const acknowledgedPopups = useLocalStorage<AcknowledgedPopups>(
      ACKNOWLEDGED_POPUPS_KEY,
      {},
      {
        // Persist synchronously so an acknowledgement isn't lost if the page
        // reloads immediately after clicking
        flush: 'sync',
        serializer: {
          read: (value: string) => {
            try {
              return pruneExpiredAcknowledgements(
                JSON.parse(value) as AcknowledgedPopups
              )
            } catch {
              return {}
            }
          },
          write: (value: AcknowledgedPopups) => JSON.stringify(value),
        },
        onError: (e) => {
          // Read/parse errors are already swallowed by the serializer (and
          // corrupt data gets overwritten by the pruned write-back below), so
          // this fires mainly for write failures like quota — log only, don't
          // destroy existing acknowledgements
          console.error('Error persisting acknowledged popups to storage', e)
        },
      }
    )
    /**
     * Sets the ref AND writes localStorage explicitly. VueUse's useStorage
     * pauses its own write-watcher until nextTick after every write (same-tab
     * custom event handling), so an assignment landing in the same tick as a
     * previous write — e.g. an ack immediately followed by a websocket
     * update — would otherwise silently skip persisting.
     */
    function persistAcknowledgements(next: AcknowledgedPopups): void {
      acknowledgedPopups.value = next
      localStorage.setItem(ACKNOWLEDGED_POPUPS_KEY, JSON.stringify(next))
    }

    // Write the pruned value back so expired entries are removed from
    // localStorage entirely, not just ignored in memory
    persistAcknowledgements(
      pruneExpiredAcknowledgements(acknowledgedPopups.value)
    )

    // An acknowledgement for a notification that is currently banner-type is
    // meaningless — drop it for ALL users (the invalidation is about the
    // notification, not the viewer). This makes a popup -> banner -> popup
    // toggle re-prompt users: the switch to banner clears acks as soon as a
    // client observes it (initial fetch or websocket update). Runs sync so
    // the localStorage write happens immediately
    watch(
      notifications,
      () => {
        const next: AcknowledgedPopups = {}
        for (const [userIdString, userAcks] of Object.entries(
          acknowledgedPopups.value
        )) {
          const nextUserAcks: UserAcknowledgedPopups = {}
          for (const [idString, activeTo] of Object.entries(userAcks)) {
            const live = notifications.value.get(Number(idString))
            const isBannerNow =
              live !== undefined &&
              live.display_type !== GlobalNotificationDisplayType.Popup
            if (!isBannerNow) nextUserAcks[Number(idString)] = activeTo
          }
          if (Object.keys(nextUserAcks).length > 0) {
            next[Number(userIdString)] = nextUserAcks
          }
        }
        if (JSON.stringify(next) !== JSON.stringify(acknowledgedPopups.value)) {
          persistAcknowledgements(next)
        }
      },
      { deep: true, immediate: true, flush: 'sync' }
    )

    function acknowledgePopup(id: number): void {
      const notification = notifications.value.get(id)
      // Only known notifications can be acknowledged — storing an unknown id
      // would persist a never-expiring (null active_to) entry forever
      if (!notification) return
      const userId = currentUserId.value
      // Without a user id we can't record the ack for the right user — and
      // the popup is never shown in that state (see unacknowledgedPopups)
      if (userId === undefined) return
      const pruned = pruneExpiredAcknowledgements(acknowledgedPopups.value)
      persistAcknowledgements({
        ...pruned,
        [userId]: {
          ...(pruned[userId] ?? {}),
          [id]: notification.active_to ?? null,
        },
      })
    }

    // Popup-type notifications never render as banners — they only show as
    // the forced dialog, and show nothing once acknowledged. An absent
    // display_type means banner (the API default), so filter on "not popup"
    const bannerNotifications = computed((): GlobalNotification[] => {
      return activeNotifications.value.filter(
        (n) => n.display_type !== GlobalNotificationDisplayType.Popup
      )
    })

    const unacknowledgedPopups = computed((): GlobalNotification[] => {
      const userId = currentUserId.value
      // Never show a dialog whose acknowledgement we couldn't record for the
      // right user (userInfo loads shortly after login)
      if (userId === undefined) return []
      const userAcks = acknowledgedPopups.value[userId] ?? {}
      return activeNotifications.value
        .filter((n) => n.display_type === GlobalNotificationDisplayType.Popup)
        .filter((n) => !(n.id in userAcks))
        .sort((a, b) => a.id - b.id)
    })

    return {
      notifications,
      update,
      activeNotifications,
      bannerNotifications,
      acknowledgedPopups,
      acknowledgePopup,
      unacknowledgedPopups,
    }
  }
)
